
import { defineComponent, ref, onMounted, onUnmounted, nextTick, computed } from 'vue'
import { useRoute } from 'vue-router'
import { createIMService } from '@/utils/im'
import { tryDecryptHex as resolveHxPass } from '@/utils/aesCrypto'
import { emojiMap, emojiList, emojiToTextMap } from '@/utils/emoji'
import newChatApi from '@/api/newChat'
import moment from 'moment'
import { message as antMessage } from 'ant-design-vue'
import { SmileOutlined, PictureOutlined, PaperClipOutlined } from '@ant-design/icons-vue'
import FdLoading from '@/components/FdLoading.vue'
import ChatFileAttachment from '@/components/chat/ChatFileAttachment.vue'
import ChatWhatsApp from '@/components/chat/ChatWhatsApp.vue'
import type { AdviserInfo } from '@/components/chat/types'
import { getOneToOneAvatar, getOneToOneNickname } from '@/components/chat/chatUser'
import { downloadChatFile } from '@/utils/chatFile'
import { getParameterValue } from '../../services/commonFunction'
import { zh_tran } from '@/utils/lang'
// Shorten global message duration to 1.5s
antMessage.config({
  duration: 1.5
})

interface ChatMessage {
  id?: string
  serverMsgId?: string
  // 后端用于去重的引用 ID，例如 getSendMsg 返回的 messages[].reply_to.id
  replyToId?: string | number
  from: string
  to: string
  msg: string
  time: number
  isRecalled?: boolean
  type?: string
  url?: string
  fileName?: string
  isMe?: boolean
  avatar?: string
  isRead?: boolean
  category?: number
}

/** list 接口 category 与消息类型映射 */
const MSG_CATEGORY = {
  TEXT: 1,
  IMAGE: 2,
  VOICE: 3,
  VIDEO: 4,
  FILE: 5,
  RECALLED: 8,
  QUESTIONS: 99,
  QUESTIONS_GROUP: 999,
} as const

const FILTERED_MSG_CATEGORIES = new Set<number>([
  MSG_CATEGORY.VOICE,
  MSG_CATEGORY.QUESTIONS,
  MSG_CATEGORY.QUESTIONS_GROUP,
])

function resolveMessageByCategory(m: any): {
  type: string
  url: string
  msg: string
  isRecalled: boolean
} | null {
  const cat = Number(m.category)
  if (FILTERED_MSG_CATEGORIES.has(cat)) return null

  if (cat === MSG_CATEGORY.RECALLED) {
    return { type: 'txt', url: '', msg: '', isRecalled: true }
  }

  let type = 'txt'
  let url = ''
  const content = m.content || ''

  switch (cat) {
    case MSG_CATEGORY.IMAGE:
      type = 'img'
      url = content
      break
    case MSG_CATEGORY.VIDEO:
      type = 'video'
      url = content
      break
    case MSG_CATEGORY.FILE:
      type = 'file'
      url = content
      break
    default:
      type = 'txt'
      break
  }

  const msg =
    type === 'img' || type === 'file' || type === 'video' ? '' : content
  return { type, url, msg, isRecalled: false }
}

/** 环信推送 data 常为 { cmd: "service", result: { category, content, id, ... } } */
function parseWsServiceResult(data: unknown): Record<string, any> | null {
  if (data == null || data === '') return null
  let parsed: any = data
  if (typeof data === 'string') {
    const trimmed = data.trim()
    if (!trimmed.startsWith('{')) return null
    try {
      parsed = JSON.parse(trimmed)
    } catch {
      return null
    }
  }
  if (!parsed || typeof parsed !== 'object') return null
  if (parsed.cmd === 'service' && parsed.result && typeof parsed.result === 'object') {
    return parsed.result
  }
  return null
}

function parseWsMessageTime(rawTime: unknown): number {
  if (rawTime == null || rawTime === '') return Date.now()
  if (typeof rawTime === 'string' && rawTime.includes('-')) {
    const t = moment(rawTime).valueOf()
    return Number.isFinite(t) ? t : Date.now()
  }
  const timeNum = typeof rawTime === 'string' ? parseInt(rawTime, 10) : Number(rawTime)
  if (!Number.isFinite(timeNum)) return Date.now()
  return timeNum > 1000000000000 ? timeNum : timeNum * 1000
}

export default defineComponent({
  name: 'NewChat',
  components: {
    SmileOutlined,
    PictureOutlined,
    PaperClipOutlined,
    FdLoading,
    ChatFileAttachment,
    ChatWhatsApp,
  },
  setup() {
    const IM_PROJECT = 'newChat' as const
    const im = createIMService(IM_PROJECT)
    const isCurrentIMEvent = (e: any) => e.detail?._imProject === IM_PROJECT

    const route = useRoute()
    const FD_APP_SOURCE = 'fdgmg'
    const CS_SOURCE = '1'

    // 路由 token 即接口 ukey，兼容旧链接 ukey 参数
    const getUkey = () => (route.query.token as string) || ''
    const isCustomerServiceMode = computed(() => route.query.source === CS_SOURCE)
    const getChatSource = () => {
      const source = route.query.source as string
      if (source === CS_SOURCE) return CS_SOURCE
      return source || FD_APP_SOURCE
    }
    const getChatHeaders = () => {
      const token = route.query.token as string
      return token ? { token } : {}
    }
    const isFdImOk = (res: any) => {
      if (!res) return false
      const msg = String(res.message ?? res.msg ?? '').trim()
      if (msg === '操作失败' || msg === 'fail' || msg === 'failed') return false
      return (
        res.code === 0 ||
        res.code === 1 ||
        msg === 'success' ||
        msg === '操作成功' ||
        res.info === 'success'
      )
    }

    const username = ref('')
    const password = ref('')
    const targetUser = ref('') // 客服 service_uid

    const applyChatUserInfo = (cs: Record<string, any> = {}) => {
      if (isCustomerServiceMode.value) {
        currentChatUserInfo.value = {
          head_portrait: cs.head_portrait || '',
          nickname: cs.nickName || '',
          user_name: cs.nickName || '',
          uid: cs.uid,
          csType: cs.csType,
        }
        return
      }

      const nickname = getOneToOneNickname(cs)
      currentChatUserInfo.value = {
        head_portrait: getOneToOneAvatar(cs),
        nickname,
        user_name: nickname,
        uid: cs.uid,
        csType: cs.csType,
      }
    }

    const serviceNickName = computed(() => {
      return currentChatUserInfo.value.nickname || '在线客服'
    })

    const serviceHeadPortrait = computed(() => {
      return currentChatUserInfo.value.head_portrait || ''
    })
    
    const isConnected = ref(false)
    const inputText = ref('')
    const messages = ref<ChatMessage[]>([])
    const messageListRef = ref<HTMLElement | null>(null)
    // 记录已出现的消息 ID（serverMsgId / id），用于全局去重
    const seenMessageIds = new Set<string>()
    const sending = ref(false)
    const textareaRef = ref<any>(null)
    
    const isLoadingHistory = ref(false)
    const hasMoreHistory = ref(true)
    const offlineIcon = require('@/assets/deepLoadingFd.png')
    const initLoading = ref(true)
    const fileInput = ref<HTMLInputElement | null>(null)
    const docFileInput = ref<HTMLInputElement | null>(null)
    const showEmoji = ref(false)
    const isKickedOffline = ref(false)
    const languageTraditional = ref(false)
    
    const currentUserInfo = ref<any>({})
    const currentChatUserInfo = ref<any>({})
    const enableWhatsApp = false

    const handleAdviserInfo = (info: AdviserInfo) => {
      const nickname = info.nickname || info.user_name
      const avatar = info.head_portrait
      currentChatUserInfo.value = {
        ...currentChatUserInfo.value,
        head_portrait: avatar || currentChatUserInfo.value.head_portrait,
        nickname: nickname || currentChatUserInfo.value.nickname,
        user_name: nickname || currentChatUserInfo.value.user_name,
      }
    }

    const adviserNickName = computed(() => {
      return currentChatUserInfo.value.nickname || '1v1服务'
    })

    const getOtherPartyAvatar = (): string => {
      return currentChatUserInfo.value.head_portrait || ''
    }

    const getMessageAvatar = (msg: ChatMessage): string => {
      if (msg.isMe) {
        return msg.avatar || currentUserInfo.value.head_portrait || ''
      }
      return getOtherPartyAvatar() || msg.avatar || ''
    }

    const adviserHeadPortrait = computed(() => getOtherPartyAvatar())

    const adviserTitle = computed(() => {
      return currentChatUserInfo.value?.csType || ''
    })

    const getDisplayName = (msg: ChatMessage) => {
      if (msg.isMe) {
        return currentUserInfo.value.nickname || currentUserInfo.value.user_name || currentUserInfo.value.name || msg.from
      }
      return currentChatUserInfo.value.nickname || msg.from
    }


    // 低版本环境（如早期 Windows）可能不支持彩色 emoji
    const supportsEmoji = ref(true)
    // Windows 7 需要使用图片替代 emoji 字符
    const useEmojiImage = ref(false)

    // 统一的 emoji 匹配正则（用于图片或文本回退）
    const emojiRegex = /[\uD83C][\uDF00-\uDFFF]|[\uD83D][\uDC00-\uDE4F]|[\uD83D][\uDE80-\uDEFF]|[\u2600-\u26FF]|[\u2700-\u27BF]|[\uD83E][\uDD00-\uDDFF]/g

    // 将 emoji 字符转换为图片 URL（使用 Twemoji CDN）
    const emojiToImage = (emoji: string): string => {
        // 将 emoji 字符转换为 Unicode 码点
        const codePoint = emoji.codePointAt(0)?.toString(16)
        if (!codePoint) return emoji
        // 使用 Twemoji CDN（Twitter 开源的 emoji 图片库）
        return `https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${codePoint}.svg`
    }

    // 将文本中的 emoji 字符转换为图片 HTML
    const convertEmojiToImage = (text: string): string => {
        return text.replace(emojiRegex, (emoji) => {
            // 处理代理对（surrogate pairs）
            let codePoint: number | undefined
            if (emoji.length === 2) {
                // 代理对
                const high = emoji.charCodeAt(0)
                const low = emoji.charCodeAt(1)
                codePoint = (high - 0xD800) * 0x400 + (low - 0xDC00) + 0x10000
            } else {
                codePoint = emoji.codePointAt(0)
            }
            if (!codePoint) return emoji
            const codePointHex = codePoint.toString(16)
            // 转义 HTML 特殊字符（用于 alt 属性）
            const safeEmoji = emoji.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
            return `<img src="https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${codePointHex}.svg" alt="${safeEmoji}" class="emoji-img" style="width: 20px; height: 20px; vertical-align: middle; display: inline-block;" />`
        })
    }

    const parseEmoji = (msg: string) => {
        if (!msg) return ''
        // 先将 [文本] 格式转换为 emoji 字符
        let result = msg.replace(/\[(.*?)\]/g, (match) => {
            return emojiMap[match] || match
        })
        
        // 如果是不支持 emoji 的环境，直接返回原文本
        if (!supportsEmoji.value) return result
        
        // 如果是 Windows 7，将 emoji 字符转换为图片
        if (useEmojiImage.value) {
            return convertEmojiToImage(result)
        }
        
        return result
    }

    const applyLocaleTransform = () => {
        if (!languageTraditional.value) return
        nextTick(() => zh_tran('t'))
    }

    const normalizeTimestamp = (ts?: number | string) => {
        if (!ts && ts !== 0) return undefined
        if (typeof ts === 'string') {
            const num = Number(ts)
            if (!isNaN(num)) {
                return num < 100000000000 ? num * 1000 : num
            }
            const parsed = Date.parse(ts)
            return isNaN(parsed) ? undefined : parsed
        }
        if (typeof ts === 'number') {
            return ts < 100000000000 ? ts * 1000 : ts
        }
        return undefined
    }

    const formatTime = (timestamp: number | string) => {
        if (timestamp === undefined || timestamp === null || timestamp === '') return ''

        let m: any = null

        if (typeof timestamp === 'string') {
            // 先尝试按字符串时间解析（如 2025-12-15 16:40:50）
            m = moment(timestamp)
            if (!m.isValid()) {
                const normalized = normalizeTimestamp(timestamp)
                if (!normalized) return String(timestamp)
                m = moment(normalized)
            }
        } else {
            const normalized = normalizeTimestamp(timestamp)
            if (!normalized) return ''
            m = moment(normalized)
        }

        if (!m || !m.isValid()) return ''

        const now = moment()
        // 如果与当前年份相同，则不展示年份，否则展示年份
        return m.year() === now.year()
            ? m.format('MM-DD HH:mm:ss')
            : m.format('YYYY-MM-DD HH:mm:ss')
    }
    
    const shouldShowTime = (index: number) => {
        if (index === 0) return true
        const currentMsg = messages.value[index]
        const prevMsg = messages.value[index - 1]
        const currTime = normalizeTimestamp(currentMsg.time)
        const prevTime = normalizeTimestamp(prevMsg.time)
        if (!currTime || !prevTime) return true
        return (currTime - prevTime) > 5 * 60 * 1000
    }

    const handleSend = async (e?: KeyboardEvent) => {
      if (e && !e.shiftKey && e.key === 'Enter') {
          e.preventDefault()
      }
      
      if (!inputText.value.trim()) return
      
      // 检查字数限制
      if (inputText.value.length > 500) {
          antMessage.warning('消息内容不能超过500个字')
          return
      }
      
      if (!targetUser.value) {
          antMessage.warning('No target user found')
          return
      }

      sending.value = true
      const messageContent = inputText.value // 保存消息内容，避免清空后丢失
      try {
          const res: any = await newChatApi.sendMessage({
              data: {
                  ukey: getUkey(),
                  service_uid: targetUser.value,
                  content: messageContent,
                  category: 1,
                  from: 1,
                  app_source: FD_APP_SOURCE,
                  device: 3,
                  channel: 2,
                  source: getChatSource(),
                  voice_time: 0,
                  platform: 2,
              },
              headers: getChatHeaders(),
          })
          if (isFdImOk(res)) {
              antMessage.success('发送成功')
              
              // 优先使用后端返回的 messages 做展示，避免与 websocket 重复
              const apiMessages = (res.messages || res.data?.messages) as any[] | undefined
              if (Array.isArray(apiMessages) && apiMessages.length > 0) {
                  apiMessages.forEach((m: any, index: number) => {
                      const replyToId = m.reply_to && m.reply_to.id ? m.reply_to.id : undefined
                      const replyToIdStr = replyToId != null ? String(replyToId) : undefined
                      
                      // 如果 websocket 已经基于 data.id 渲染了消息（对应 reply_to.id），这里就不再重复添加
                      if (replyToIdStr) {
                          const duplicated = messages.value.some((ex: ChatMessage) => 
                              (ex.serverMsgId && String(ex.serverMsgId) === replyToIdStr) ||
                              (ex.id && String(ex.id) === replyToIdStr)
                          )
                          if (duplicated) {
                              return
                          }
                      }

                      const sid = m.msg_id ? String(m.msg_id) : (m.id ? String(m.id) : undefined)
                      
                      // 在添加消息之前，先检查该消息 ID 是否已存在（防止与 onMessageSent 重复）
                      if (sid) {
                          // 1. 检查全局去重集合
                          if (seenMessageIds.has(sid)) {
                              return
                          }
                          // 2. 检查当前消息列表
                          const alreadyExists = messages.value.some((ex: ChatMessage) =>
                              (ex.serverMsgId && String(ex.serverMsgId) === sid) ||
                              (ex.id && String(ex.id) === sid)
                          )
                          if (alreadyExists) {
                              seenMessageIds.add(sid)
                              return
                          }
                          // 3. 先记录 ID 到 seenMessageIds，防止 onMessageSent 重复添加
                          seenMessageIds.add(sid)
                      }
                      
                      const newMsg: ChatMessage = {
                        ...currentUserInfo.value,
                        id: sid || String(Date.now()),
                        // getSendMsg 返回的 msg_id 才是真正的消息 ID，用于撤回消息
                        serverMsgId: sid,
                        replyToId,
                        from: username.value,
                        to: targetUser.value,
                        msg: m.content ?? messageContent,
                        time: m.create_time || m.message_time || Date.now(),
                        type: m.type || 'txt',
                        url: m.url || '',
                        isMe: true,
                        isRead: true,
                        avatar: currentUserInfo.value.head_portrait
                      }
                      
                      // 直接添加到 messages（确保立即显示）
                      messages.value.push(newMsg)
                      
                  })
              } else {
                  const d = res.data || {}
                  const msgId = d.msgId != null ? String(d.msgId) : (d.msg_id != null ? String(d.msg_id) : undefined)
                  
                  // 在添加消息之前，先检查该消息 ID 是否已存在（防止与 onMessageSent 重复）
                  if (msgId) {
                      // 1. 检查全局去重集合
                      if (seenMessageIds.has(msgId)) {
                          return
                      }
                      // 2. 检查当前消息列表
                      const alreadyExists = messages.value.some((ex: ChatMessage) =>
                          (ex.serverMsgId && String(ex.serverMsgId) === msgId) ||
                          (ex.id && String(ex.id) === msgId)
                      )
                      if (alreadyExists) {
                          seenMessageIds.add(msgId)
                          return
                      }
                      // 3. 先记录 ID 到 seenMessageIds，防止 onMessageSent 重复添加
                      seenMessageIds.add(msgId)
                  }
                  
                  const newMsg: ChatMessage = {
                    ...currentUserInfo.value,
                    id: msgId || String(Date.now()),
                    serverMsgId: msgId || '',
                    from: username.value,
                    to: targetUser.value,
                    msg: messageContent,
                    time: Date.now(),
                    type: 'txt',
                    isMe: true,
                    isRead: true,
                    avatar: currentUserInfo.value.head_portrait
                  }
                  
                  // 直接添加到 messages（确保立即显示）
                  messages.value.push(newMsg)
              }

              inputText.value = '' // 在添加消息后再清空输入框
              
              // 强制触发 Vue 的响应式更新
              nextTick(() => {
                scrollToBottom()
              })
              applyLocaleTransform()
          } else {
              // 发送失败，不添加消息，只提示错误
              antMessage.error(res.message || res.msg || '发送失败')
          }
      } catch (error) {
          console.error(error)
          antMessage.error('Failed to send message')
      } finally {
          sending.value = false
      }
    }
    
    const handleEmojiClick = (emoji: string) => {
        // 在 Windows 7 上（useEmojiImage 为 true），使用文本格式而不是 emoji 字符，避免输入框显示乱码
        const emojiText = useEmojiImage.value 
            ? (emojiToTextMap[emoji] || emoji)  // Windows 7 使用文本格式
            : emoji  // 正常系统使用 emoji 字符
        
        showEmoji.value = false
        
        // 在光标位置插入 emoji，而不是追加到末尾
        nextTick(() => {
          if (textareaRef.value) {
            // Ant Design Vue 的 textarea 组件，需要获取内部的 textarea 元素
            let textarea: HTMLTextAreaElement | null = null
            
            // 尝试多种方式获取原生 textarea 元素
            if (textareaRef.value.$el) {
              // Vue 3 组件实例
              textarea = textareaRef.value.$el.querySelector('textarea') || textareaRef.value.$el
            } else if (textareaRef.value instanceof HTMLTextAreaElement) {
              // 直接是原生元素
              textarea = textareaRef.value
            } else if (textareaRef.value.resizableTextArea) {
              // Ant Design Vue 的 textarea 组件结构
              textarea = textareaRef.value.resizableTextArea.textArea
            }
            
            if (textarea && typeof textarea.setSelectionRange === 'function') {
              // 获取当前光标位置
              const start = textarea.selectionStart || 0
              const end = textarea.selectionEnd || start
              
              // 在光标位置插入 emoji
              const text = inputText.value
              const newText = text.substring(0, start) + emojiText + text.substring(end)
              inputText.value = newText
              
              // 设置光标位置到插入后的位置
              const newCursorPos = start + emojiText.length
              textarea.focus()
              setTimeout(() => {
                if (textarea) {
                  textarea.setSelectionRange(newCursorPos, newCursorPos)
                }
              }, 0)
            } else {
              // 如果无法获取光标位置，则追加到末尾（兼容处理）
              inputText.value += emojiText
              if (textareaRef.value && typeof textareaRef.value.focus === 'function') {
                textareaRef.value.focus()
              }
            }
          }
        })
    }
    
    const appendUploadedMessage = (
      url: string,
      msgType: string,
      sendRes: any,
      fileName?: string
    ) => {
      const apiMessages = (sendRes.messages || sendRes.data?.messages) as any[] | undefined
      if (Array.isArray(apiMessages) && apiMessages.length > 0) {
        apiMessages.forEach((m: any) => {
          const replyToId = m.reply_to && m.reply_to.id ? m.reply_to.id : undefined
          const replyToIdStr = replyToId != null ? String(replyToId) : undefined

          if (replyToIdStr) {
            const duplicated = messages.value.some((ex: ChatMessage) =>
              (ex.serverMsgId && String(ex.serverMsgId) === replyToIdStr) ||
              (ex.id && String(ex.id) === replyToIdStr)
            )
            if (duplicated) return
          }

          const sid = m.msg_id ? String(m.msg_id) : (m.id ? String(m.id) : undefined)

          if (sid) {
            if (seenMessageIds.has(sid)) return
            const alreadyExists = messages.value.some((ex: ChatMessage) =>
              (ex.serverMsgId && String(ex.serverMsgId) === sid) ||
              (ex.id && String(ex.id) === sid)
            )
            if (alreadyExists) {
              seenMessageIds.add(sid)
              return
            }
          }

          const newMsg: ChatMessage = {
            ...currentUserInfo.value,
            id: sid || String(Date.now()),
            serverMsgId: sid,
            replyToId,
            from: username.value,
            to: targetUser.value,
            msg: '',
            url: m.url || url,
            fileName: fileName || m.file_name || m.fileName,
            time: m.create_time || m.message_time || Date.now(),
            type: m.type || msgType,
            isMe: true,
            isRead: true,
            avatar: currentUserInfo.value.head_portrait
          }

          messages.value.push(newMsg)
          if (sid) seenMessageIds.add(sid)
        })
        return
      }

      const sd = sendRes.data || {}
      const msgId = sd.msgId != null ? String(sd.msgId) : (sd.msg_id != null ? String(sd.msg_id) : undefined)

      if (msgId) {
        if (seenMessageIds.has(msgId)) return
        const alreadyExists = messages.value.some((ex: ChatMessage) =>
          (ex.serverMsgId && String(ex.serverMsgId) === msgId) ||
          (ex.id && String(ex.id) === msgId)
        )
        if (alreadyExists) {
          seenMessageIds.add(msgId)
          return
        }
      }

      const newMsg: ChatMessage = {
        ...currentUserInfo.value,
        id: msgId || String(Date.now()),
        serverMsgId: msgId,
        from: username.value,
        to: targetUser.value,
        msg: '',
        url,
        fileName,
        time: Date.now(),
        type: msgType,
        isMe: true,
        isRead: true,
        avatar: currentUserInfo.value.head_portrait
      }

      messages.value.push(newMsg)
      if (msgId) seenMessageIds.add(msgId)
    }

    const uploadAndSendAttachment = async (
      file: File,
      options: { category: number; msgType: string; defaultMime: string }
    ) => {
      try {
        sending.value = true
        const fileData = await new Promise<ArrayBuffer>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as ArrayBuffer)
          reader.onerror = reject
          reader.readAsArrayBuffer(file)
        })
        const blob = new Blob([fileData], { type: file.type || options.defaultMime })
        const formData = new FormData()
        formData.append('fname', blob, file.name)
        const res: any = await newChatApi.getUpload(formData)
        if (res && res.code === 1 && res.message === '上传成功') {
          const url = res.result
          const sendRes: any = await newChatApi.sendMessage({
            data: {
              ukey: getUkey(),
              service_uid: targetUser.value,
              content: url,
              category: options.category,
              from: 1,
              app_source: FD_APP_SOURCE,
              device: 3,
              channel: 2,
              source: getChatSource(),
              voice_time: 0,
              platform: 2,
            },
            headers: getChatHeaders(),
          })

          if (isFdImOk(sendRes)) {
            antMessage.success('发送成功')
            appendUploadedMessage(
              url,
              options.msgType,
              sendRes,
              options.msgType === 'file' ? file.name : undefined
            )
            scrollToBottom()
            applyLocaleTransform()
          } else {
            antMessage.error(sendRes.message || sendRes.msg || '发送失败')
          }
        } else {
          antMessage.error(res.msg || '上传失败')
        }
      } catch (err) {
        console.error(err)
        antMessage.error('上传失败')
      } finally {
        sending.value = false
      }
    }

    const handleImageUpload = () => {
      fileInput.value?.click()
    }

    const handleDocUpload = () => {
      docFileInput.value?.click()
    }

    const onImageSelected = async (e: Event) => {
      const files = (e.target as HTMLInputElement).files
      if (files && files.length > 0) {
        await uploadAndSendAttachment(files[0], {
          category: 2,
          msgType: 'img',
          defaultMime: 'image/png',
        })
      }
      if (fileInput.value) fileInput.value.value = ''
    }

    const onDocFileSelected = async (e: Event) => {
      const files = (e.target as HTMLInputElement).files
      if (files && files.length > 0) {
        await uploadAndSendAttachment(files[0], {
          category: 5,
          msgType: 'file',
          defaultMime: 'application/octet-stream',
        })
      }
      if (docFileInput.value) docFileInput.value.value = ''
    }

    const scrollToBottom = () => {
        const doScroll = () => {
            const el = messageListRef.value
            if (!el) return
            el.scrollTop = el.scrollHeight
        }
        nextTick(() => {
            requestAnimationFrame(() => {
                nextTick(doScroll)
            })
        })
    }

    // 窗口大小变化时，直接滚动到底部
    const handleWindowResize = () => {
        // 使用 requestAnimationFrame 和 nextTick 确保在 DOM 更新后执行
        requestAnimationFrame(() => {
            nextTick(() => {
                if (messageListRef.value) {
                    messageListRef.value.scrollTop = messageListRef.value.scrollHeight
                }
            })
        })
    }

    const handleScroll = (e: Event) => {
        const target = e.target as HTMLElement
        // 当滚动条接近顶部时（例如距离顶部小于等于 50px），自动加载更多历史消息
        if (target.scrollTop <= 50 && !isLoadingHistory.value && hasMoreHistory.value) {
            handleLoadMore()
        }
    }

    const handleLoadMore = async () => {
        if (isLoadingHistory.value || !hasMoreHistory.value) return
        const ukey = getUkey()
        if (!ukey || messages.value.length === 0) return

        // 取当前第一条消息的 id 作为 last_msg_id
        const first = messages.value[0]
        const lastId = first.serverMsgId || first.id
        if (!lastId) return

        if (!targetUser.value) return
        await fetchHistory(ukey, targetUser.value, lastId, true)
    }

    const initOneToOnePage = async () => {
        await fetchChatInfo()
    }

    const fetchChatInfo = async () => {
        const acceptLang = getParameterValue('Accept-Language') || (route.query['Accept-Language'] as string) || ''
        languageTraditional.value = acceptLang === 'zh-Hant'
        const ukey = getUkey()
        if (!ukey) {
            antMessage.error('缺少 token 参数')
            initLoading.value = false
            return
        }

        try {
            const res: any = await newChatApi.getImAccount({
                data: { ukey, source: getChatSource() },
                headers: getChatHeaders(),
            })
            if (!isFdImOk(res)) {
                antMessage.error(res?.message || '获取 IM 账户失败')
                return
            }
            const data = res.data || {}
            const cs = data.custom_service || {}
            username.value = data.hx_username || ''
            const rawHxPass = data.hx_pass || ''
            const decryptedHxPass = resolveHxPass(rawHxPass)
            console.log('打印解密后的密码====',decryptedHxPass)
            password.value = decryptedHxPass

            currentUserInfo.value = {
                head_portrait: data.head_portrait,
                nickname: data.nickname,
                uid: data.uid,
                user_name: data.nickname,
            }
            applyChatUserInfo(cs)

            if (cs.uid) {
                targetUser.value = String(cs.uid)
            }

            if (username.value && password.value) {
                im.loginIMWithPassword(username.value, password.value)
                isConnected.value = true
            }

            await fetchHistory(ukey, targetUser.value)
        } catch (error) {
            console.error('Failed to get chat info', error)
            antMessage.error('Failed to load chat info')
        } finally {
            initLoading.value = false
            // 列表在 initLoading 为 false 后才挂载，须在此后再滚到底部
            nextTick(() => scrollToBottom())
        }
    }
    
    const fetchHistory = async (
        ukey: string,
        to_suid: number | string,
        last_msg_id: number | string = '',
        append: boolean = false
    ) => {
        try {
            isLoadingHistory.value = true

            const res: any = await newChatApi.getMessageList({
                data: {
                    ukey,
                    service_uid: to_suid,
                    msg_id: last_msg_id || '',
                    limit_num: 100,
                    source: getChatSource(),
                },
                headers: getChatHeaders(),
            })
            if (!isFdImOk(res)) {
                antMessage.error(res?.message || '获取历史消息失败')
                return
            }
            const raw = res.data
            let list = Array.isArray(raw) ? raw : (raw?.messages || raw?.list || [])
            if (!Array.isArray(list)) list = []
            list = list.slice().reverse()
            hasMoreHistory.value = list.length >= 100
            const userData = currentUserInfo.value || {}
            const toUserData = currentChatUserInfo.value || {}
            
            // Save current user info for later use
            currentUserInfo.value = userData
            currentChatUserInfo.value = toUserData
            
            const adaptedMessages = list
              .map((m: any) => {
                const resolved = resolveMessageByCategory(m)
                if (!resolved) return null

                const isMe = String(m.from) === '1' || m.is_me == 1 || m.is_me === true
                const otherUser = String(to_suid)
                const additionalInfo = isMe ? userData : toUserData

                return {
                    ...additionalInfo,
                    id: String(m.id),
                    serverMsgId: String(m.id),
                    from: isMe ? username.value : otherUser,
                    to: isMe ? otherUser : username.value,
                    msg: resolved.msg,
                    fileName: m.file_name || m.fileName,
                    time: m.create_time || m.message_time || Date.now(),
                    isRecalled: resolved.isRecalled,
                    type: resolved.type,
                    url: resolved.url,
                    isMe: isMe,
                    category: Number(m.category) || undefined,
                    replyToId: m.reply_to && m.reply_to.id ? m.reply_to.id : undefined,
                    avatar: additionalInfo.head_portrait
                } as ChatMessage
              })
              .filter(Boolean) as ChatMessage[]
            let displayedMessages = adaptedMessages
            
            if (!targetUser.value) {
                if (adaptedMessages.length > 0) {
                    const other = adaptedMessages.find((m: any) => !m.isMe)
                    if (other) {
                        targetUser.value = other.from
                    } else {
                        targetUser.value = adaptedMessages[0].to
                    }
                } else if (to_suid) {
                    targetUser.value = String(to_suid)
                }
            }

            console.log('打印数据====',displayedMessages)

            if (append) {
                // 追加到顶部（加载更多历史）
                const messageListEl = messageListRef.value
                const oldScrollHeight = messageListEl ? messageListEl.scrollHeight : 0

                // 去重：避免重复消息
                const existingIds = new Set(messages.value.map(m => m.id))
                const newMessages = displayedMessages.filter(m => !existingIds.has(m.id))

                if (newMessages.length > 0) {
                    // 将新消息的 ID 记录到全局去重集合
                    newMessages.forEach(m => {
                        if (m.serverMsgId) seenMessageIds.add(String(m.serverMsgId))
                        if (m.id) seenMessageIds.add(String(m.id))
                    })
                    messages.value = newMessages.concat(messages.value)
                    nextTick(() => {
                        if (messageListEl) {
                            const newScrollHeight = messageListEl.scrollHeight
                            messageListEl.scrollTop = newScrollHeight - oldScrollHeight
                        }
                    })
                }
            } else {
                // 首次加载：与 WS 已推送消息按 ID 合并，避免重复
                const prevList = messages.value.slice()
                const existingIds = new Set(
                  prevList
                    .map(m => m.serverMsgId || m.id)
                    .filter(Boolean)
                    .map(id => String(id))
                )
                const newMessages = displayedMessages.filter(m => {
                  const id = m.serverMsgId || m.id
                  if (!id) return true
                  const idStr = String(id)
                  if (existingIds.has(idStr)) return false
                  existingIds.add(idStr)
                  return true
                })
                const merged = newMessages.concat(prevList)
                messages.value = merged
                merged.forEach(m => {
                  if (m.serverMsgId) seenMessageIds.add(String(m.serverMsgId))
                  if (m.id) seenMessageIds.add(String(m.id))
                })
                scrollToBottom()
            }
            applyLocaleTransform()
        } catch (e) {
            console.error('Failed to get history', e)
        } finally {
            isLoadingHistory.value = false
        }
    }

    const getAvatarFromWsMessage = (msg: any): string => {
      const unifiedAvatar = getOtherPartyAvatar()
      if (unifiedAvatar) {
        return unifiedAvatar
      }
      if (msg.ext?.from?.head_portrait) {
        return msg.ext.from.head_portrait
      }
      return ''
    }

    // Event Listeners
    const onMessageReceived = (e: any) => {
      if (!isCurrentIMEvent(e)) return
      const msg = e.detail
      console.log('打印收到的msg====',msg)

      // 如果是自己（当前登录帐号）发出的消息，已经通过 HTTP getSendMsg 在本地回显过，
      // 这里的普通消息推送再次到达时直接忽略，避免 HTTP + WS 各回显一条导致重复。
      if (msg.from && msg.from === username.value) {
        return
      }

      const serviceResult = parseWsServiceResult(msg.data)
      const wsCategory =
        serviceResult?.category ?? msg.ext?.category ?? msg.category
      const wsContent =
        serviceResult != null
          ? (serviceResult.content ?? '')
          : (msg.data ?? msg.ext?.content ?? '')

      if (wsCategory != null && wsCategory !== '') {
        const resolved = resolveMessageByCategory({
          category: wsCategory,
          content: wsContent,
        })
        if (!resolved) return

        let wsExtId: string | undefined
        if (serviceResult?.id != null) wsExtId = String(serviceResult.id)
        else if (msg.ext?.id) wsExtId = String(msg.ext.id)
        if (wsExtId && seenMessageIds.has(wsExtId)) return
        if (wsExtId && messages.value.some(
          (m: ChatMessage) =>
            (m.id && String(m.id) === wsExtId) ||
            (m.serverMsgId && String(m.serverMsgId) === wsExtId)
        )) return

        if (!targetUser.value) targetUser.value = msg.from

        const wsAvatar =
          serviceResult?.head_portrait ||
          serviceResult?.empAvatar ||
          getAvatarFromWsMessage(msg)

        messages.value.push({
          ...currentChatUserInfo.value,
          id: wsExtId || msg.id,
          serverMsgId: wsExtId || msg.id,
          from: msg.from,
          to: msg.to,
          msg: resolved.msg,
          time: parseWsMessageTime(serviceResult?.create_time ?? msg.time),
          type: resolved.type,
          url: resolved.url,
          fileName:
            serviceResult?.file_name ||
            serviceResult?.fileName ||
            msg.ext?.file_name ||
            msg.ext?.fileName,
          isMe: false,
          isRead: true,
          isRecalled: resolved.isRecalled,
          category: Number(wsCategory) || undefined,
          avatar: wsAvatar,
        })
        if (wsExtId) seenMessageIds.add(wsExtId)
        if (msg.id) seenMessageIds.add(String(msg.id))
        scrollToBottom()
        applyLocaleTransform()
        return
      }

      // service 推送已在上面按 category 处理；此处避免把原始 JSON 当正文展示
      if (serviceResult) return

      const body = msg.bodies ? msg.bodies[0] : {}
      let type = body.type || 'txt'
      
      // 按照优先级获取消息内容：e.detail.data > e.detail.data.ext.imgs > [Message]
      let content = '[Message]'
      let url = body.url || ''
      
      if (msg.data) {
        // 如果 data 是字符串或数字，直接使用
        if (typeof msg.data === 'string' || typeof msg.data === 'number') {
          content = String(msg.data)
        } else if (msg.ext?.imgs) {
          // 如果 data 是对象，尝试获取 ext.imgs（图片消息）
          type = 'img'
          url = msg.ext.imgs
          content = '' // 图片消息不需要文本内容
        } else if (msg.ext?.pdf || msg.ext?.file) {
          type = 'file'
          url = msg.ext.pdf || msg.ext.file
          content = ''
        } else {
          // 如果 data 存在但没有 ext.imgs，使用 data 本身（转换为字符串）
          content = String(msg.data)
        }
      } else if (msg.ext?.imgs) {
        // 如果 data 没值，尝试获取 ext.imgs（图片消息）
        type = 'img'
        url = msg.ext.imgs
        content = '' // 图片消息不需要文本内容
      } else if (msg.ext?.pdf || msg.ext?.file) {
        type = 'file'
        url = msg.ext.pdf || msg.ext.file
        content = ''
      }

      if ((type === 'file' || type === 'pdf') && !url && typeof msg.data === 'string') {
        url = msg.data
        content = ''
      }

      // 检查 msg.ext.id 是否与 getChatMessage 返回的消息 id 重复
      let wsExtId: string | undefined
      if (msg.ext && msg.ext.id) {
        wsExtId = String(msg.ext.id)
      }
      
      let isExistingMessage = false
      if (wsExtId) {
        if (seenMessageIds.has(wsExtId)) {
          isExistingMessage = true
        } else {
          isExistingMessage = messages.value.some(
            (m: ChatMessage) =>
              (m.id && String(m.id) === wsExtId) ||
              (m.serverMsgId && String(m.serverMsgId) === wsExtId)
          )
        }
      }

      if (isExistingMessage) {
        console.log('消息已存在，跳过添加:', wsExtId)
        return
      }

      if (!targetUser.value) targetUser.value = msg.from

      let msgTime = Date.now()
      if (msg.time) {
        const timeNum = typeof msg.time === 'string' ? parseInt(msg.time, 10) : msg.time
        msgTime = timeNum > 1000000000000 ? timeNum : timeNum * 1000
      }

      messages.value.push({
        ...currentChatUserInfo.value,
        id: wsExtId || msg.id,
        serverMsgId: wsExtId || msg.id,
        from: msg.from,
        to: msg.to,
        msg: content,
        time: msgTime,
        type: type,
        url: url,
        fileName: msg.ext?.file_name || msg.ext?.fileName,
        isMe: false,
        isRead: true,
        avatar: getAvatarFromWsMessage(msg)
      })
      if (wsExtId) seenMessageIds.add(wsExtId)
      if (msg.id) seenMessageIds.add(String(msg.id))
      scrollToBottom()
      applyLocaleTransform()
    }

    const onMessageSent = (e: any) => {
      if (!isCurrentIMEvent(e)) return
      const msg = e.detail
      console.log('[onMessageSent] receive ws sent event', msg)
      console.log('[onMessageSent] current messages.value.length:', messages.value.length)

      // 由于自己发送的消息已经通过 HTTP getSendMsg 在本地回显，这里直接忽略自己发送的 WS 回调，避免重复
      if (msg.from && msg.from === username.value) {
        console.log('[onMessageSent] ignore self sent message (already rendered via HTTP)', {
          from: msg.from,
          username: username.value
        })
        return
      }

      // 统一获取本条消息的唯一 ID（用于去重）
      const sentId = msg.serverMsgId || msg.id || msg.ext?.id
      if (sentId) {
        const idStr = String(sentId)
        // 1）全局去重集合里已经存在，说明通过 HTTP getSendMsg 已经渲染过，直接跳过
        if (seenMessageIds.has(idStr)) {
          console.log('[onMessageSent] skip because id already in seenMessageIds', { idStr })
          return
        }
        // 2）当前消息列表里已经有相同 id/serverMsgId 的消息，也视为重复
        const duplicated = messages.value.some(m =>
          (m.serverMsgId && String(m.serverMsgId) === idStr) ||
          (m.id && String(m.id) === idStr)
        )
        if (duplicated) {
          seenMessageIds.add(idStr)
          console.log('[onMessageSent] skip because id already in messages', { idStr })
          return
        }
      }

      // 3）额外检查：如果消息内容、发送者、接收者都相同，且时间相近（5秒内），也视为重复
      // 这是为了防止 ID 不一致但实际是同一消息的情况
      const msgContent = msg.msg || msg.data || ''
      const msgFrom = msg.from || ''
      const msgTo = msg.to ? String(msg.to) : ''
      const recentDuplicate = messages.value.some(m => {
        if (m.isMe && m.from === msgFrom && m.to === msgTo) {
          const contentMatch = (m.msg === msgContent || m.msg === String(msg.data))
          const timeDiff = Math.abs((m.time || 0) - (msg.time || Date.now()))
          // 如果内容相同且时间差在5秒内，认为是同一条消息
          if (contentMatch && timeDiff < 5000) {
            return true
          }
        }
        return false
      })
      if (recentDuplicate) {
        // 如果找到重复，也记录 ID（如果有的话）
        if (sentId) {
          seenMessageIds.add(String(sentId))
        }
        console.log('[onMessageSent] skip because found recent duplicate by content/time', {
          msgContent,
          msgFrom,
          msgTo
        })
        return
      }

      // websocket 返回的 time 字段需要转换（从秒转毫秒）
      // 先将字符串转换为数字，然后判断是否需要从秒转毫秒
      let msgTime = Date.now()
      if (msg.time) {
          const timeNum = typeof msg.time === 'string' ? parseInt(msg.time, 10) : msg.time
          msgTime = timeNum > 1000000000000 ? timeNum : timeNum * 1000
      }
      messages.value.push({
        id: msg.id,
        serverMsgId: msg.serverMsgId,
        from: msg.from,
        to: msg.to,
        msg: msg.msg,
        time: msgTime,
        type: msg.type || 'txt',
        url: msg.url,
        isMe: true,
        isRead: true
      })

      if (sentId) {
        seenMessageIds.add(String(sentId))
      }
      console.log('[onMessageSent] add new ws message', {
        sentId,
        msg,
        messagesLenBefore: messages.value.length
      })
      
      scrollToBottom()
      applyLocaleTransform()
    }

    // 为不支持 vh/calc 的低版本浏览器设置固定高度
    const fixOldBrowserScroll = () => {
      const ua = navigator.userAgent
      // 检测不支持 vh 或 calc 的浏览器（IE8 及以下）
      const isOldBrowser = /MSIE [1-8]\./.test(ua) || /Trident\/[1-3]\./.test(ua)
      
      if (isOldBrowser) {
        const setHeights = () => {
          const windowHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
          const sectionHeight = windowHeight
          
          // 设置容器高度
          const containerEl = document.querySelector('.chat-container') as HTMLElement
          const cardEl = document.querySelector('.chat-card') as HTMLElement
          const sectionEl = document.querySelector('.chat-section') as HTMLElement
          const wrapperEl = document.querySelector('.chat-wrapper') as HTMLElement
          
          if (containerEl) containerEl.style.height = windowHeight + 'px'
          if (cardEl) cardEl.style.height = windowHeight + 'px'
          if (sectionEl) sectionEl.style.height = sectionHeight + 'px'
          if (wrapperEl) wrapperEl.style.height = sectionHeight + 'px'
          
          // 设置 message-list 高度
          const messageListEl = messageListRef.value
          if (messageListEl) {
            messageListEl.style.height = (sectionHeight - 41 - 168) + 'px' // 减去 chat-header 和 input-area
          }
        }
        
        nextTick(() => {
          setHeights()
          window.addEventListener('resize', setHeights)
        })
      }
    }

    // 被踢下线事件处理
    const onKickedOffline = (e: any) => {
      if (!isCurrentIMEvent(e)) return
      console.log('User kicked offline')
      isKickedOffline.value = true
      isConnected.value = false
    }

    const handleOfflineRelogin = () => {
      // 关闭蒙层并触发客户端登录
      isKickedOffline.value = false
      try {
        // 和 FdIndex.vue 中保持一致
        window.open('gw://funccall?funcname=OnDoCommand')
      } catch (e) {
        console.error(e)
        // 兜底刷新页面
        window.location.reload()
      }
    }

    onMounted(() => {
      // 检测 Windows 版本
      const ua = navigator.userAgent
      // Windows NT 5.1 = XP, 5.2 = Server 2003, 6.0 = Vista
      const isVeryOldWindows = /Windows NT (5\.1|5\.2|6\.0)/.test(ua)
      // Windows NT 6.1 = Windows 7
      const isWindows7 = /Windows NT 6\.1/.test(ua)
      
      if (isVeryOldWindows) {
        // 非常老的系统（XP/Vista）完全禁用 emoji
        supportsEmoji.value = false
      } else if (isWindows7) {
        // Windows 7 使用图片替代 emoji 字符，确保正常显示
        supportsEmoji.value = true
        useEmojiImage.value = true
      }

      // 修复低版本浏览器的滚动问题
      fixOldBrowserScroll()

      const initPromise = isCustomerServiceMode.value ? fetchChatInfo() : initOneToOnePage()
      initPromise.finally(() => {
        applyLocaleTransform()
        scrollToBottom()
      })
      window.addEventListener('on-im-message', onMessageReceived)
      window.addEventListener('on-im-message-sent', onMessageSent)
      window.addEventListener('on-im-kicked-offline', onKickedOffline)
      window.addEventListener('resize', handleWindowResize)
    })

    onUnmounted(() => {
      window.removeEventListener('on-im-message', onMessageReceived)
      window.removeEventListener('on-im-message-sent', onMessageSent)
      window.removeEventListener('on-im-kicked-offline', onKickedOffline)
      window.removeEventListener('resize', handleWindowResize)
    })

    const handleFileDownload = async (msg: ChatMessage) => {
      if (!msg.url) return
      try {
        await downloadChatFile(msg.url, msg.fileName)
      } catch (err) {
        console.error('download file failed', err)
        antMessage.error('下载失败，请稍后重试')
      }
    }

    return {
      isCustomerServiceMode,
      enableWhatsApp,
      currentUserInfo,
      handleAdviserInfo,
      adviserNickName,
      adviserHeadPortrait,
      adviserTitle,
      username,
      serviceNickName,
      serviceHeadPortrait,
      inputText,
      messages,
      isConnected,
      initLoading,
      isKickedOffline,
      getDisplayName,
      getMessageAvatar,
      handleSend,
      messageListRef,
      handleScroll,
      isLoadingHistory,
      shouldShowTime,
      formatTime,
      sending,
      supportsEmoji,
      useEmojiImage,
      emojiToImage,
      showEmoji,
      emojiList,
      handleEmojiClick,
      handleImageUpload,
      handleDocUpload,
      onImageSelected,
      onDocFileSelected,
      fileInput,
      docFileInput,
      parseEmoji,
      textareaRef,
      hasMoreHistory,
      offlineIcon,
      handleLoadMore,
      handleOfflineRelogin,
      handleFileDownload
    }
  }
})
